import React, { useState } from 'react';
import {
  ArrowLeft,
  Film,
  Calendar,
  MessageSquare,
  Users,
  Check,
  Trash2,
  Edit2,
  Plus,
  ExternalLink,
  Globe,
  Zap,
  Copy,
  ArrowUp,
  ArrowDown,
  Save,
  Loader2,
  Sparkles,
  Layout,
  Link as LinkIcon,
  Video,
  Book,
  Award,
  Image as ImageIcon,
} from 'lucide-react';
import {
  MentorAreaConfig,
  MentorModule,
  MentorLesson,
  MentorEvent,
  MentorStudent,
  BioLinkItem,
  MentorBioConfig,
} from '../../types/mentorship';
import { BioQRCodeModal } from './BioQRCodeModal';
import ImageUploader from '../ImageUploader';
import {
  WhatsAppIcon,
  InstagramIcon,
  YouTubeIcon,
  LinkedInIcon,
  TikTokIcon,
  XTwitterIcon,
  GitHubIcon,
} from './SocialIcons';
import { useToast } from '../../contexts/ToastContext';

interface MentorEditorPageProps {
  config: MentorAreaConfig;
  onSaveConfig: (newConfig: MentorAreaConfig) => Promise<{ success: boolean; message: string }> | void;
  onBack: () => void;
}

export const MentorEditorPage: React.FC<MentorEditorPageProps> = ({
  config,
  onSaveConfig,
  onBack,
}) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'geral' | 'bio' | 'modulos' | 'eventos' | 'suporte' | 'alunos'>('geral');
  const [formData, setFormData] = useState<MentorAreaConfig>({ ...config });
  const [saving, setSaving] = useState(false);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // States para Módulos / Aulas
  const [newModuleTitle, setNewModuleTitle] = useState('');
  const [newModuleDesc, setNewModuleDesc] = useState('');
  const [newModuleCover, setNewModuleCover] = useState('');

  const [selectedModuleIdForLesson, setSelectedModuleIdForLesson] = useState<string>('');
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [newLessonYoutube, setNewLessonYoutube] = useState('');
  const [newLessonDuration, setNewLessonDuration] = useState('10 min');
  const [newLessonDesc, setNewLessonDesc] = useState('');

  // States para Eventos
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventDate, setNewEventDate] = useState('');
  const [newEventTime, setNewEventTime] = useState('20:00');
  const [newEventDesc, setNewEventDesc] = useState('');
  const [newEventUrl, setNewEventUrl] = useState('');

  // States para Alunos
  const [newStudentEmail, setNewStudentEmail] = useState('');
  const [newStudentName, setNewStudentName] = useState('');

  // States para Bio Links (Linktree)
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const [newBioTitle, setNewBioTitle] = useState('');
  const [newBioSubtitle, setNewBioSubtitle] = useState('');
  const [newBioUrl, setNewBioUrl] = useState('');
  const [newBioIcon, setNewBioIcon] = useState('Zap');
  const [newBioCustomIconUrl, setNewBioCustomIconUrl] = useState('');
  const [newBioFeatured, setNewBioFeatured] = useState(false);
  const [newBioBadge, setNewBioBadge] = useState('🔥 RECOMENDADO');

  // FAQ state
  const [newFaqQuestion, setNewFaqQuestion] = useState('');
  const [newFaqAnswer, setNewFaqAnswer] = useState('');

  const accentColor = formData.branding?.accentColor || '#0FFF55';
  
  const currentBio = formData.bioConfig || {
    isEnabled: true,
    slug: formData.slug,
    bioTitle: formData.mentorName,
    bioSubtitle: formData.mentorTitle,
    bioAvatarUrl: formData.mentorAvatarUrl,
    socials: {
      instagram: 'https://instagram.com',
      youtube: 'https://youtube.com',
      whatsapp: 'https://wa.me/5511999999999',
      linkedin: 'https://linkedin.com',
      website: 'https://masterclub.vip',
    },
    links: [],
  };

  const bioPageUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/bio-${formData.slug}`
    : `https://masterclub.vip/bio-${formData.slug}`;

  const mentoriaPageUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/mentoria-${formData.slug}`
    : `https://masterclub.vip/mentoria-${formData.slug}`;

  const handleCopyBioUrl = () => {
    navigator.clipboard.writeText(bioPageUrl);
    setCopiedLink(true);
    showToast({
      type: 'success',
      title: 'Link Copiado!',
      message: 'O link da sua Bio/Linktree foi copiado para a área de transferência.',
    });
    setTimeout(() => setCopiedLink(false), 2000);
  };

  // Salvar alterações no banco de dados
  const handleSaveToDatabase = async () => {
    setSaving(true);
    try {
      const res = await onSaveConfig(formData);
      showToast({
        type: 'success',
        title: 'Alterações Salvas!',
        message: (res && typeof res === 'object' && res.message) ? res.message : 'Suas configurações foram salvas com sucesso no banco de dados.',
      });
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro ao Salvar',
        message: err?.message || 'Não foi possível salvar as alterações no banco de dados.',
      });
    } finally {
      setSaving(false);
    }
  };

  // Handlers Módulos e Aulas
  const handleAddModule = () => {
    if (!newModuleTitle.trim()) return;
    const newMod: MentorModule = {
      id: `mod-${Date.now()}`,
      order: formData.modules.length + 1,
      title: newModuleTitle.toUpperCase(),
      description: newModuleDesc,
      coverImageUrl: newModuleCover || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80&w=600',
      accentGlowColor: accentColor,
      lessons: [],
    };
    const updated = { ...formData, modules: [...formData.modules, newMod] };
    setFormData(updated);
    setNewModuleTitle('');
    setNewModuleDesc('');
    setNewModuleCover('');
  };

  const handleDeleteModule = (modId: string) => {
    if (!confirm('Deseja realmente excluir este módulo e suas aulas?')) return;
    const updated = { ...formData, modules: formData.modules.filter((m) => m.id !== modId) };
    setFormData(updated);
  };

  const handleAddLesson = (modId: string) => {
    if (!newLessonTitle.trim() || !newLessonYoutube.trim()) return;
    const newLes: MentorLesson = {
      id: `les-${Date.now()}`,
      order: 1,
      title: newLessonTitle,
      videoUrl: newLessonYoutube,
      durationText: newLessonDuration || '10 min',
      thumbnailUrl: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=600',
      publishedAtText: `Publicado em ${new Date().toLocaleDateString('pt-BR')}`,
      description: newLessonDesc,
      isCompleted: false,
    };

    const updatedModules = formData.modules.map((m) => {
      if (m.id === modId) {
        return { ...m, lessons: [...m.lessons, newLes] };
      }
      return m;
    });

    const updated = { ...formData, modules: updatedModules };
    setFormData(updated);
    setNewLessonTitle('');
    setNewLessonYoutube('');
    setNewLessonDesc('');
    setSelectedModuleIdForLesson('');
  };

  const handleDeleteLesson = (modId: string, lesId: string) => {
    const updatedModules = formData.modules.map((m) => {
      if (m.id === modId) {
        return { ...m, lessons: m.lessons.filter((l) => l.id !== lesId) };
      }
      return m;
    });
    const updated = { ...formData, modules: updatedModules };
    setFormData(updated);
  };

  // Handlers Eventos
  const handleAddEvent = () => {
    if (!newEventTitle.trim() || !newEventDate.trim()) return;
    const newEv: MentorEvent = {
      id: `ev-${Date.now()}`,
      title: newEventTitle,
      dateText: newEventDate,
      timeText: newEventTime,
      description: newEventDesc,
      meetingUrl: newEventUrl,
      type: 'live',
      isLiveNow: false,
    };
    const updated = { ...formData, events: [...formData.events, newEv] };
    setFormData(updated);
    setNewEventTitle('');
    setNewEventDate('');
    setNewEventDesc('');
    setNewEventUrl('');
  };

  const handleDeleteEvent = (evId: string) => {
    const updated = { ...formData, events: formData.events.filter((e) => e.id !== evId) };
    setFormData(updated);
  };

  // Handlers Alunos
  const handleAddStudent = () => {
    if (!newStudentEmail.trim() || !newStudentName.trim()) return;
    const newSt: MentorStudent = {
      id: `st-${Date.now()}`,
      name: newStudentName,
      email: newStudentEmail,
      avatarUrl: `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(newStudentName)}`,
      joinedAtText: `Adicionado em ${new Date().toLocaleDateString('pt-BR')}`,
      status: 'active',
    };
    const updated = { ...formData, students: [...(formData.students || []), newSt] };
    setFormData(updated);
    setNewStudentName('');
    setNewStudentEmail('');
  };

  const handleDeleteStudent = (stId: string) => {
    const updated = {
      ...formData,
      students: (formData.students || []).filter((s) => s.id !== stId),
    };
    setFormData(updated);
  };

  // Handlers Bio Links (Linktree)
  const handleSaveBioLinkItem = () => {
    if (!newBioTitle.trim() || !newBioUrl.trim()) return;

    const trimmedCustom = newBioCustomIconUrl.trim();
    const finalIcon = newBioIcon === 'Custom' ? (trimmedCustom || 'Globe') : newBioIcon;
    const finalThumbnail = trimmedCustom || undefined;

    let updatedLinks: BioLinkItem[] = [...(currentBio.links || [])];

    if (editingLinkId) {
      updatedLinks = updatedLinks.map((item) => {
        if (item.id === editingLinkId) {
          return {
            ...item,
            title: newBioTitle.trim(),
            subtitle: newBioSubtitle.trim() || undefined,
            url: newBioUrl.trim(),
            icon: finalIcon,
            thumbnailUrl: finalThumbnail,
            iconUrl: finalThumbnail,
            isFeatured: newBioFeatured,
            badgeText: newBioFeatured ? (newBioBadge.trim() || undefined) : undefined,
          };
        }
        return item;
      });
    } else {
      const newItem: BioLinkItem = {
        id: `link-${Date.now()}`,
        title: newBioTitle.trim(),
        subtitle: newBioSubtitle.trim() || undefined,
        url: newBioUrl.trim(),
        icon: finalIcon,
        thumbnailUrl: finalThumbnail,
        iconUrl: finalThumbnail,
        isFeatured: newBioFeatured,
        badgeText: newBioFeatured ? (newBioBadge.trim() || undefined) : undefined,
      };
      updatedLinks.push(newItem);
    }

    const updatedBio: MentorBioConfig = { ...currentBio, links: updatedLinks };
    setFormData({ ...formData, bioConfig: updatedBio });

    // Reset Form
    setEditingLinkId(null);
    setNewBioTitle('');
    setNewBioSubtitle('');
    setNewBioUrl('');
    setNewBioIcon('Zap');
    setNewBioCustomIconUrl('');
    setNewBioFeatured(false);
    setNewBioBadge('🔥 RECOMENDADO');
  };

  const handleEditBioLinkItem = (item: BioLinkItem) => {
    setEditingLinkId(item.id);
    setNewBioTitle(item.title);
    setNewBioSubtitle(item.subtitle || '');
    setNewBioUrl(item.url);
    const customImg =
      item.thumbnailUrl ||
      item.iconUrl ||
      (item.icon &&
      (item.icon.startsWith('http') || item.icon.startsWith('/') || item.icon.startsWith('data:'))
        ? item.icon
        : '');

    if (customImg) {
      setNewBioIcon('Custom');
      setNewBioCustomIconUrl(customImg);
    } else {
      setNewBioIcon(item.icon || 'Zap');
      setNewBioCustomIconUrl('');
    }
    setNewBioFeatured(!!item.isFeatured);
    setNewBioBadge(item.badgeText || '🔥 RECOMENDADO');
  };

  const handleDeleteBioLinkItem = (linkId: string) => {
    const updatedLinks = (currentBio.links || []).filter((l) => l.id !== linkId);
    const updatedBio: MentorBioConfig = { ...currentBio, links: updatedLinks };
    setFormData({ ...formData, bioConfig: updatedBio });
  };

  const handleMoveBioLink = (linkId: string, direction: 'up' | 'down') => {
    const links = [...(currentBio.links || [])];
    const index = links.findIndex((l) => l.id === linkId);
    if (index < 0) return;

    if (direction === 'up' && index > 0) {
      const temp = links[index];
      links[index] = links[index - 1];
      links[index - 1] = temp;
    } else if (direction === 'down' && index < links.length - 1) {
      const temp = links[index];
      links[index] = links[index + 1];
      links[index + 1] = temp;
    }

    const updatedBio: MentorBioConfig = { ...currentBio, links };
    setFormData({ ...formData, bioConfig: updatedBio });
  };

  // FAQ Handlers
  const handleAddFaq = () => {
    if (!newFaqQuestion.trim() || !newFaqAnswer.trim()) return;
    const currentFaq = formData.support?.faq || [];
    const updatedFaq = [...currentFaq, { question: newFaqQuestion, answer: newFaqAnswer }];
    setFormData({
      ...formData,
      support: { ...formData.support, faq: updatedFaq },
    });
    setNewFaqQuestion('');
    setNewFaqAnswer('');
  };

  const handleDeleteFaq = (index: number) => {
    const currentFaq = formData.support?.faq || [];
    const updatedFaq = currentFaq.filter((_, idx) => idx !== index);
    setFormData({
      ...formData,
      support: { ...formData.support, faq: updatedFaq },
    });
  };

  // Render da Barra de Salvar no Rodapé de cada Aba
  const renderSaveFooter = () => (
    <div className="sticky bottom-0 z-20 mt-8 -mx-6 -mb-6 p-4 bg-[#17181c]/95 backdrop-blur-md border-t border-white/10 flex items-center justify-between gap-4 rounded-b-2xl shadow-2xl">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <Sparkles className="w-4 h-4 text-[#0FFF55]" />
        <span>Altere as informações desejadas e clique ao lado para sincronizar no banco de dados.</span>
      </div>

      <button
        type="button"
        onClick={handleSaveToDatabase}
        disabled={saving}
        className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0FFF55] hover:bg-[#0dd149] text-black font-extrabold text-sm transition-all active:scale-95 shadow-lg shadow-[#0FFF55]/20 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
      >
        {saving ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin text-black" />
            <span>Salvando no Banco...</span>
          </>
        ) : (
          <>
            <Save className="w-4 h-4 text-black" />
            <span>Salvar Alterações no Banco de Dados</span>
          </>
        )}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0d0e11] text-gray-100 flex flex-col font-sans">
      {/* Top Bar da Tela de Edição */}
      <header className="sticky top-0 z-30 bg-[#121316]/95 backdrop-blur-md border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1c1e24] hover:bg-white/10 text-gray-300 hover:text-white text-xs font-semibold border border-white/10 transition-all active:scale-95 cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Voltar para Mentoria</span>
          </button>
          <div className="h-6 w-px bg-white/10" />
          <div className="flex items-center gap-3">
            <img
              src={formData.mentorAvatarUrl}
              alt={formData.mentorName}
              className="w-9 h-9 rounded-full object-cover ring-2 ring-[#0FFF55]"
            />
            <div>
              <h1 className="text-sm font-bold text-white flex items-center gap-2">
                {formData.mentorName}
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#0FFF55]/20 text-[#0FFF55] font-bold border border-[#0FFF55]/30">
                  PAINEL DE EDIÇÃO
                </span>
              </h1>
              <p className="text-xs text-gray-400 truncate max-w-xs md:max-w-md">{formData.courseTitle}</p>
            </div>
          </div>
        </div>

        {/* Links de Atalho para Páginas Públicas */}
        <div className="flex items-center gap-3">
          <a
            href={mentoriaPageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1c1e24] hover:bg-white/10 text-gray-300 text-xs font-medium border border-white/10 transition-colors"
          >
            <Globe className="w-3.5 h-3.5 text-cyan-400" />
            <span>Ver Mentoria Pública</span>
            <ExternalLink className="w-3 h-3 text-gray-500" />
          </a>

          <a
            href={bioPageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1c1e24] hover:bg-white/10 text-gray-300 text-xs font-medium border border-white/10 transition-colors"
          >
            <Zap className="w-3.5 h-3.5 text-[#0FFF55]" />
            <span>Ver Bio / Linktree</span>
            <ExternalLink className="w-3 h-3 text-gray-500" />
          </a>

          <button
            onClick={handleSaveToDatabase}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0FFF55] hover:bg-[#0dd149] text-black font-extrabold text-xs transition-all active:scale-95 shadow-md shadow-[#0FFF55]/20 disabled:opacity-50 cursor-pointer"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            <span>Salvar no Banco</span>
          </button>
        </div>
      </header>

      {/* Conteúdo Principal com Sidebar de Navegação */}
      <div className="flex-1 flex max-w-7xl w-full mx-auto p-4 md:p-6 gap-6">
        {/* Navegação por Abas (Sidebar Esquerda) */}
        <aside className="w-64 flex-shrink-0 space-y-2">
          <div className="bg-[#121316] border border-white/10 rounded-2xl p-3 space-y-1 sticky top-24">
            <p className="px-3 py-2 text-[10px] font-bold text-gray-400 tracking-wider uppercase">
              Configurações da Mentoria
            </p>

            <button
              onClick={() => setActiveTab('geral')}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                activeTab === 'geral'
                  ? 'bg-[#0FFF55]/10 text-[#0FFF55] border border-[#0FFF55]/30'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Layout className="w-4 h-4" />
              <span>Geral & Branding</span>
            </button>

            <button
              onClick={() => setActiveTab('bio')}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                activeTab === 'bio'
                  ? 'bg-[#0FFF55]/10 text-[#0FFF55] border border-[#0FFF55]/30'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <LinkIcon className="w-4 h-4" />
              <span>Bio & Linktree</span>
            </button>

            <button
              onClick={() => setActiveTab('modulos')}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                activeTab === 'modulos'
                  ? 'bg-[#0FFF55]/10 text-[#0FFF55] border border-[#0FFF55]/30'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Film className="w-4 h-4" />
              <span>Módulos & Aulas</span>
            </button>

            <button
              onClick={() => setActiveTab('eventos')}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                activeTab === 'eventos'
                  ? 'bg-[#0FFF55]/10 text-[#0FFF55] border border-[#0FFF55]/30'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Calendar className="w-4 h-4" />
              <span>Eventos & Lives</span>
            </button>

            <button
              onClick={() => setActiveTab('suporte')}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                activeTab === 'suporte'
                  ? 'bg-[#0FFF55]/10 text-[#0FFF55] border border-[#0FFF55]/30'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              <span>Suporte & FAQ</span>
            </button>

            <button
              onClick={() => setActiveTab('alunos')}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                activeTab === 'alunos'
                  ? 'bg-[#0FFF55]/10 text-[#0FFF55] border border-[#0FFF55]/30'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Users className="w-4 h-4" />
              <span>Alunos & Acessos</span>
            </button>
          </div>
        </aside>

        {/* Área de Conteúdo da Aba Ativa */}
        <main className="flex-1 min-w-0 bg-[#121316] border border-white/10 rounded-2xl p-6 relative flex flex-col justify-between">
          <div>
            {/* TAB 1: GERAL & BRANDING */}
            {activeTab === 'geral' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <Layout className="w-5 h-5 text-[#0FFF55]" />
                    <span>Informações Gerais & Identidade Visual</span>
                  </h2>
                  <p className="text-xs text-gray-400 mt-1">
                    Configure o nome do mentor, título do curso, URLs das imagens de marca e cores do tema.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1.5">Nome do Mentor</label>
                    <input
                      type="text"
                      value={formData.mentorName}
                      onChange={(e) => setFormData({ ...formData, mentorName: e.target.value })}
                      className="w-full px-3.5 py-2.5 bg-[#1a1c23] border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#0FFF55]"
                      placeholder="Ex: Vitor Terra"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1.5">Título / Especialidade</label>
                    <input
                      type="text"
                      value={formData.mentorTitle}
                      onChange={(e) => setFormData({ ...formData, mentorTitle: e.target.value })}
                      className="w-full px-3.5 py-2.5 bg-[#1a1c23] border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#0FFF55]"
                      placeholder="Ex: Especialista em E-commerce & Tráfego"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1.5">URL da Foto de Perfil (Avatar)</label>
                    <input
                      type="text"
                      value={formData.mentorAvatarUrl}
                      onChange={(e) => setFormData({ ...formData, mentorAvatarUrl: e.target.value })}
                      className="w-full px-3.5 py-2.5 bg-[#1a1c23] border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#0FFF55]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1.5">Slug Único da Mentoria (URL)</label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      className="w-full px-3.5 py-2.5 bg-[#1a1c23] border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#0FFF55]"
                      placeholder="vitor-terra"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-300 mb-1.5">Nome do Curso / Mentoria</label>
                    <input
                      type="text"
                      value={formData.courseTitle}
                      onChange={(e) => setFormData({ ...formData, courseTitle: e.target.value })}
                      className="w-full px-3.5 py-2.5 bg-[#1a1c23] border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#0FFF55]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1.5">Cor de Destaque (Accent Color)</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={accentColor}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            branding: { ...formData.branding, accentColor: e.target.value },
                          })
                        }
                        className="w-10 h-10 rounded-lg cursor-pointer bg-transparent border-0"
                      />
                      <input
                        type="text"
                        value={accentColor}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            branding: { ...formData.branding, accentColor: e.target.value },
                          })
                        }
                        className="w-full px-3.5 py-2.5 bg-[#1a1c23] border border-white/10 rounded-xl text-sm text-white font-mono focus:outline-none focus:border-[#0FFF55]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1.5">URL da Banner de Capa</label>
                    <input
                      type="text"
                      value={formData.branding?.bannerUrl || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          branding: { ...formData.branding, bannerUrl: e.target.value },
                        })
                      }
                      className="w-full px-3.5 py-2.5 bg-[#1a1c23] border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#0FFF55]"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: BIO & LINKTREE */}
            {activeTab === 'bio' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <div>
                    <h2 className="text-lg font-bold text-white flex items-center gap-2">
                      <LinkIcon className="w-5 h-5 text-[#0FFF55]" />
                      <span>Configurações da Bio & Linktree</span>
                    </h2>
                    <p className="text-xs text-gray-400 mt-1">
                      Gerencie o perfil público de links do mentor exibido em <code className="text-[#0FFF55] font-mono">{bioPageUrl}</code>
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleCopyBioUrl}
                    className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-[#1c1e24] hover:bg-white/10 text-xs font-semibold border border-white/10 text-white transition-all active:scale-95 cursor-pointer"
                  >
                    {copiedLink ? <Check className="w-4 h-4 text-[#0FFF55]" /> : <Copy className="w-4 h-4" />}
                    <span>{copiedLink ? 'Copiado!' : 'Copiar Link da Bio'}</span>
                  </button>
                </div>

                {/* Formulário de Cabeçalho da Bio */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-[#181a20] p-4 rounded-xl border border-white/5">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Título da Bio</label>
                    <input
                      type="text"
                      value={currentBio.bioTitle || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bioConfig: { ...currentBio, bioTitle: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-[#0FFF55]"
                      placeholder="Ex: Vitor Terra"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Subtítulo / Descrição da Bio</label>
                    <input
                      type="text"
                      value={currentBio.bioSubtitle || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bioConfig: { ...currentBio, bioSubtitle: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-[#0FFF55]"
                      placeholder="Ex: Fundador da MasterClub & Mentor de E-commerce"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Foto da Bio (Avatar URL)</label>
                    <input
                      type="text"
                      value={currentBio.bioAvatarUrl || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bioConfig: { ...currentBio, bioAvatarUrl: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-[#0FFF55]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Instagram URL</label>
                    <input
                      type="text"
                      value={currentBio.socials?.instagram || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bioConfig: {
                            ...currentBio,
                            socials: { ...currentBio.socials, instagram: e.target.value },
                          },
                        })
                      }
                      className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-[#0FFF55]"
                      placeholder="https://instagram.com/seu-perfil"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">WhatsApp URL / Número</label>
                    <input
                      type="text"
                      value={currentBio.socials?.whatsapp || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bioConfig: {
                            ...currentBio,
                            socials: { ...currentBio.socials, whatsapp: e.target.value },
                          },
                        })
                      }
                      className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-[#0FFF55]"
                      placeholder="https://wa.me/5511999999999"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">YouTube URL</label>
                    <input
                      type="text"
                      value={currentBio.socials?.youtube || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bioConfig: {
                            ...currentBio,
                            socials: { ...currentBio.socials, youtube: e.target.value },
                          },
                        })
                      }
                      className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-[#0FFF55]"
                      placeholder="https://youtube.com/@seu-canal"
                    />
                  </div>
                </div>

                {/* Gerenciador de Links Personalizados */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-white flex items-center justify-between">
                    <span>Links Personalizados do Linktree</span>
                    <span className="text-xs text-gray-400 font-normal">
                      {currentBio.links?.length || 0} links configurados
                    </span>
                  </h3>

                  {/* Form Adicionar/Editar Link */}
                  <div className="bg-[#17181c] p-4 rounded-xl border border-white/10 space-y-3">
                    <h4 className="text-xs font-bold text-[#0FFF55]">
                      {editingLinkId ? '✏️ Editar Link' : '➕ Adicionar Novo Link'}
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] text-gray-300 mb-1">Título do Link *</label>
                        <input
                          type="text"
                          value={newBioTitle}
                          onChange={(e) => setNewBioTitle(e.target.value)}
                          className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                          placeholder="Ex: ⚡ Mentoria Exclusiva VIP"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] text-gray-300 mb-1">Subtítulo (opcional)</label>
                        <input
                          type="text"
                          value={newBioSubtitle}
                          onChange={(e) => setNewBioSubtitle(e.target.value)}
                          className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                          placeholder="Ex: Acompanhamento de 1 ano comigo"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-[11px] text-gray-300 mb-1">URL de Destino *</label>
                        <input
                          type="text"
                          value={newBioUrl}
                          onChange={(e) => setNewBioUrl(e.target.value)}
                          className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white font-mono"
                          placeholder="https://..."
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] text-gray-300 mb-1">Ícone</label>
                        <select
                          value={newBioIcon}
                          onChange={(e) => setNewBioIcon(e.target.value)}
                          className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                        >
                          <option value="Zap">⚡ Raio (Destaque)</option>
                          <option value="MessageSquare">💬 WhatsApp / Mensagem</option>
                          <option value="Globe">🌐 Website / Link</option>
                          <option value="Instagram">📸 Instagram</option>
                          <option value="Video">🎥 Vídeo / YouTube</option>
                          <option value="TikTok">🎵 TikTok</option>
                          <option value="LinkedIn">💼 LinkedIn</option>
                          <option value="XTwitter">🐦 X / Twitter</option>
                          <option value="GitHub">🐙 GitHub</option>
                          <option value="Book">📚 Curso / Livro</option>
                          <option value="Award">🏆 Certificado / Conquista</option>
                          <option value="Custom">🖼️ Ícone / Imagem Personalizada</option>
                        </select>
                      </div>

                      <div className="flex items-center gap-3 pt-4">
                        <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newBioFeatured}
                            onChange={(e) => setNewBioFeatured(e.target.checked)}
                            className="rounded border-white/20 bg-transparent text-[#0FFF55] focus:ring-0"
                          />
                          <span>Destacar este link (Badge)</span>
                        </label>
                      </div>
                    </div>

                    {/* Bloco de Imagem/Ícone Personalizado */}
                    {(newBioIcon === 'Custom' || newBioCustomIconUrl) && (
                      <div className="p-3.5 rounded-xl bg-[#101114] border border-[#0FFF55]/30 space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                          <div className="flex items-center gap-2">
                            <ImageIcon className="w-4 h-4 text-[#0FFF55]" />
                            <span className="text-xs font-bold text-white">Ícone / Imagem Personalizada para o Botão</span>
                          </div>
                          <span className="text-[10px] text-[#0FFF55] font-semibold bg-[#0FFF55]/10 px-2 py-0.5 rounded border border-[#0FFF55]/20 self-start sm:self-auto">
                            💡 Recomendado: 64x64px a 128x128px (PNG transparente ou WebP)
                          </span>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-3">
                          {/* Live Preview */}
                          <div className="flex flex-col items-center gap-1 flex-shrink-0">
                            <div className="w-11 h-11 rounded-full bg-white/5 border border-[#0FFF55]/50 flex items-center justify-center overflow-hidden shadow-md shadow-[#0FFF55]/10">
                              {newBioCustomIconUrl ? (
                                <img
                                  src={newBioCustomIconUrl}
                                  alt="Preview"
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    (e.target as HTMLElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <ImageIcon className="w-5 h-5 text-gray-500" />
                              )}
                            </div>
                            <span className="text-[9px] text-gray-400">Preview</span>
                          </div>

                          <div className="flex-1 w-full space-y-2">
                            <div className="flex items-center gap-2">
                              <input
                                type="url"
                                placeholder="Cole a URL do ícone (ex: https://.../logo.png)"
                                value={newBioCustomIconUrl}
                                onChange={(e) => {
                                  setNewBioCustomIconUrl(e.target.value);
                                  if (e.target.value) setNewBioIcon('Custom');
                                }}
                                className="flex-1 px-3 py-2 bg-[#17181c] border border-white/10 rounded-lg text-xs text-white font-mono"
                              />
                              {newBioCustomIconUrl && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNewBioCustomIconUrl('');
                                    if (newBioIcon === 'Custom') setNewBioIcon('Zap');
                                  }}
                                  className="px-2.5 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-semibold transition-all cursor-pointer"
                                >
                                  Limpar
                                </button>
                              )}
                            </div>

                            <ImageUploader
                              bucketName="mentorias"
                              label="Ou Envie o Arquivo da Imagem"
                              value={newBioCustomIconUrl}
                              onChange={(url) => {
                                setNewBioCustomIconUrl(url);
                                setNewBioIcon('Custom');
                              }}
                              options={{ maxWidth: 256, maxHeight: 256, quality: 0.9, format: 'webp' }}
                              placeholder="Clique para enviar PNG / WebP / SVG..."
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                      {editingLinkId && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingLinkId(null);
                            setNewBioTitle('');
                            setNewBioSubtitle('');
                            setNewBioUrl('');
                            setNewBioIcon('Zap');
                            setNewBioCustomIconUrl('');
                          }}
                          className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs text-gray-300 cursor-pointer"
                        >
                          Cancelar
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleSaveBioLinkItem}
                        className="px-4 py-1.5 rounded-lg bg-[#0FFF55] hover:bg-[#0dd149] text-black text-xs font-bold cursor-pointer"
                      >
                        {editingLinkId ? 'Atualizar Link na Lista' : 'Adicionar Link à Lista'}
                      </button>
                    </div>
                  </div>

                  {/* Lista de Links Existentes */}
                  <div className="space-y-2">
                    {(currentBio.links || []).map((link, idx) => {
                      const customImg =
                        link.thumbnailUrl ||
                        link.iconUrl ||
                        (link.icon &&
                        (link.icon.startsWith('http') || link.icon.startsWith('/') || link.icon.startsWith('data:'))
                          ? link.icon
                          : null);

                      const renderItemIcon = () => {
                        if (customImg) {
                          return (
                            <img
                              src={customImg}
                              alt={link.title}
                              className="w-full h-full object-cover rounded-lg"
                              onError={(e) => {
                                (e.target as HTMLElement).style.display = 'none';
                              }}
                            />
                          );
                        }
                        switch (link.icon) {
                          case 'Zap':
                          case 'Lightning':
                            return <Zap className="w-4 h-4 text-[#0FFF55]" />;
                          case 'MessageSquare':
                          case 'WhatsApp':
                          case 'whatsapp':
                            return <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />;
                          case 'Globe':
                          case 'Website':
                          case 'Web':
                            return <Globe className="w-4 h-4 text-cyan-400" />;
                          case 'Video':
                          case 'Youtube':
                          case 'youtube':
                            return <YouTubeIcon className="w-4 h-4 text-[#FF0000]" />;
                          case 'Instagram':
                          case 'instagram':
                            return <InstagramIcon className="w-4 h-4 text-[#E4405F]" />;
                          case 'LinkedIn':
                          case 'linkedin':
                            return <LinkedInIcon className="w-4 h-4 text-[#0A66C2]" />;
                          case 'TikTok':
                          case 'tiktok':
                            return <TikTokIcon className="w-4 h-4 text-[#00f2fe]" />;
                          case 'XTwitter':
                          case 'Twitter':
                          case 'twitter':
                            return <XTwitterIcon className="w-4 h-4 text-white" />;
                          case 'GitHub':
                          case 'github':
                            return <GitHubIcon className="w-4 h-4 text-white" />;
                          case 'Book':
                            return <Book className="w-4 h-4 text-purple-400" />;
                          case 'Award':
                            return <Award className="w-4 h-4 text-amber-400" />;
                          default:
                            return <ExternalLink className="w-4 h-4 text-[#0FFF55]" />;
                        }
                      };

                      return (
                        <div
                          key={link.id || idx}
                          className="flex items-center justify-between p-3 rounded-xl bg-[#17181c] border border-white/10 hover:border-white/20 transition-all"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex flex-col gap-1">
                              <button
                                type="button"
                                onClick={() => handleMoveBioLink(link.id, 'up')}
                                disabled={idx === 0}
                                className="p-1 hover:bg-white/10 rounded disabled:opacity-20 text-gray-400 cursor-pointer"
                              >
                                <ArrowUp className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleMoveBioLink(link.id, 'down')}
                                disabled={idx === (currentBio.links?.length || 0) - 1}
                                className="p-1 hover:bg-white/10 rounded disabled:opacity-20 text-gray-400 cursor-pointer"
                              >
                                <ArrowDown className="w-3 h-3" />
                              </button>
                            </div>

                            <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {renderItemIcon()}
                            </div>

                            <div>
                              <p className="text-xs font-bold text-white flex items-center gap-2">
                                {link.title}
                                {link.isFeatured && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#0FFF55]/20 text-[#0FFF55] font-bold">
                                    {link.badgeText || 'DESTAQUE'}
                                  </span>
                                )}
                              </p>
                              {link.subtitle && <p className="text-[11px] text-gray-400">{link.subtitle}</p>}
                              <p className="text-[10px] text-gray-500 font-mono truncate max-w-xs">{link.url}</p>
                            </div>
                          </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditBioLinkItem(link)}
                            className="p-2 hover:bg-white/10 text-gray-300 hover:text-white rounded-lg transition-colors cursor-pointer"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteBioLinkItem(link.id)}
                            className="p-2 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors cursor-pointer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              </div>
            )}

            {/* TAB 3: MÓDULOS & AULAS */}
            {activeTab === 'modulos' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <Film className="w-5 h-5 text-[#0FFF55]" />
                    <span>Gerenciador de Módulos & Aulas em Vídeo</span>
                  </h2>
                  <p className="text-xs text-gray-400 mt-1">
                    Organize o conteúdo do curso da mentoria, dividindo em módulos e vídeos do YouTube.
                  </p>
                </div>

                {/* Form Adicionar Módulo */}
                <div className="bg-[#17181c] p-4 rounded-xl border border-white/10 space-y-3">
                  <h3 className="text-xs font-bold text-[#0FFF55]">➕ Criar Novo Módulo</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] text-gray-300 mb-1">Título do Módulo *</label>
                      <input
                        type="text"
                        value={newModuleTitle}
                        onChange={(e) => setNewModuleTitle(e.target.value)}
                        className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                        placeholder="MÓDULO 01 - FUNDAMENTOS DO TRÁFEGO"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-300 mb-1">URL da Imagem de Capa</label>
                      <input
                        type="text"
                        value={newModuleCover}
                        onChange={(e) => setNewModuleCover(e.target.value)}
                        className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                        placeholder="https://images.unsplash.com/..."
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-[11px] text-gray-300 mb-1">Descrição Breve do Módulo</label>
                      <input
                        type="text"
                        value={newModuleDesc}
                        onChange={(e) => setNewModuleDesc(e.target.value)}
                        className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                        placeholder="Aprenda do zero como estruturar suas campanhas..."
                      />
                    </div>
                  </div>
                  <div className="flex justify-end pt-1">
                    <button
                      type="button"
                      onClick={handleAddModule}
                      className="px-4 py-1.5 rounded-lg bg-[#0FFF55] hover:bg-[#0dd149] text-black text-xs font-bold cursor-pointer"
                    >
                      Adicionar Módulo
                    </button>
                  </div>
                </div>

                {/* Lista de Módulos */}
                <div className="space-y-4">
                  {formData.modules.map((module) => (
                    <div key={module.id} className="bg-[#17181c] border border-white/10 rounded-xl p-4 space-y-3">
                      <div className="flex items-center justify-between border-b border-white/10 pb-3">
                        <div className="flex items-center gap-3">
                          <img
                            src={module.coverImageUrl}
                            alt={module.title}
                            className="w-12 h-8 rounded object-cover border border-white/10"
                          />
                          <div>
                            <h4 className="text-sm font-bold text-white">{module.title}</h4>
                            <p className="text-xs text-gray-400">{module.lessons.length} aulas cadastradas</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setSelectedModuleIdForLesson(selectedModuleIdForLesson === module.id ? '' : module.id)}
                            className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-semibold text-white flex items-center gap-1.5 cursor-pointer"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            <span>Add Aula</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteModule(module.id)}
                            className="p-1.5 hover:bg-red-500/20 text-red-400 rounded-lg cursor-pointer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Subform Add Aula neste Módulo */}
                      {selectedModuleIdForLesson === module.id && (
                        <div className="bg-[#101114] p-3 rounded-lg border border-[#0FFF55]/30 space-y-2">
                          <h5 className="text-xs font-bold text-[#0FFF55]">➕ Adicionar Aula a "{module.title}"</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={newLessonTitle}
                              onChange={(e) => setNewLessonTitle(e.target.value)}
                              placeholder="Título da Aula *"
                              className="px-3 py-1.5 bg-[#17181c] border border-white/10 rounded text-xs text-white"
                            />
                            <input
                              type="text"
                              value={newLessonYoutube}
                              onChange={(e) => setNewLessonYoutube(e.target.value)}
                              placeholder="Link do Vídeo (YouTube) *"
                              className="px-3 py-1.5 bg-[#17181c] border border-white/10 rounded text-xs text-white"
                            />
                            <input
                              type="text"
                              value={newLessonDuration}
                              onChange={(e) => setNewLessonDuration(e.target.value)}
                              placeholder="Duração (ex: 15 min)"
                              className="px-3 py-1.5 bg-[#17181c] border border-white/10 rounded text-xs text-white"
                            />
                            <input
                              type="text"
                              value={newLessonDesc}
                              onChange={(e) => setNewLessonDesc(e.target.value)}
                              placeholder="Descrição breve da aula"
                              className="px-3 py-1.5 bg-[#17181c] border border-white/10 rounded text-xs text-white"
                            />
                          </div>
                          <div className="flex justify-end gap-2 pt-1">
                            <button
                              type="button"
                              onClick={() => setSelectedModuleIdForLesson('')}
                              className="px-3 py-1 rounded bg-white/10 text-xs text-gray-300 cursor-pointer"
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleAddLesson(module.id)}
                              className="px-3 py-1 rounded bg-[#0FFF55] text-black text-xs font-bold cursor-pointer"
                            >
                              Salvar Aula
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Lista de Aulas do Módulo */}
                      <div className="space-y-1.5 pl-2">
                        {module.lessons.map((lesson, lIdx) => (
                          <div
                            key={lesson.id || lIdx}
                            className="flex items-center justify-between p-2 rounded bg-[#101114] border border-white/5"
                          >
                            <div className="flex items-center gap-2">
                              <Video className="w-3.5 h-3.5 text-[#0FFF55]" />
                              <span className="text-xs text-gray-200 font-medium">{lesson.title}</span>
                              <span className="text-[10px] text-gray-500 font-mono">({lesson.durationText})</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteLesson(module.id, lesson.id)}
                              className="p-1 hover:bg-red-500/20 text-red-400 rounded cursor-pointer"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TAB 4: EVENTOS & LIVES */}
            {activeTab === 'eventos' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#0FFF55]" />
                    <span>Eventos, Encontros e Lives</span>
                  </h2>
                  <p className="text-xs text-gray-400 mt-1">
                    Agende os encontros síncronos ao vivo com seus alunos de mentoria.
                  </p>
                </div>

                {/* Form Adicionar Evento */}
                <div className="bg-[#17181c] p-4 rounded-xl border border-white/10 space-y-3">
                  <h3 className="text-xs font-bold text-[#0FFF55]">➕ Cadastrar Novo Encontro / Live</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] text-gray-300 mb-1">Título do Encontro *</label>
                      <input
                        type="text"
                        value={newEventTitle}
                        onChange={(e) => setNewEventTitle(e.target.value)}
                        className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                        placeholder="Mentoria em Grupo - Análise de Campanhas"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-300 mb-1">Data do Evento *</label>
                      <input
                        type="text"
                        value={newEventDate}
                        onChange={(e) => setNewEventDate(e.target.value)}
                        className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                        placeholder="Quarta, 25 de Outubro"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-300 mb-1">Horário</label>
                      <input
                        type="text"
                        value={newEventTime}
                        onChange={(e) => setNewEventTime(e.target.value)}
                        className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                        placeholder="20:00"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-300 mb-1">Link da Sala (Zoom/Google Meet)</label>
                      <input
                        type="text"
                        value={newEventUrl}
                        onChange={(e) => setNewEventUrl(e.target.value)}
                        className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white font-mono"
                        placeholder="https://meet.google.com/..."
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-[11px] text-gray-300 mb-1">Pauta / Descrição</label>
                      <input
                        type="text"
                        value={newEventDesc}
                        onChange={(e) => setNewEventDesc(e.target.value)}
                        className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                        placeholder="Tragam suas contas de anúncios para auditarmos ao vivo..."
                      />
                    </div>
                  </div>
                  <div className="flex justify-end pt-1">
                    <button
                      type="button"
                      onClick={handleAddEvent}
                      className="px-4 py-1.5 rounded-lg bg-[#0FFF55] hover:bg-[#0dd149] text-black text-xs font-bold cursor-pointer"
                    >
                      Cadastrar Encontro
                    </button>
                  </div>
                </div>

                {/* Lista de Eventos */}
                <div className="space-y-3">
                  {formData.events.map((event) => (
                    <div
                      key={event.id}
                      className="flex items-center justify-between p-3.5 rounded-xl bg-[#17181c] border border-white/10"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-lg bg-[#0FFF55]/10 text-[#0FFF55]">
                          <Calendar className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="text-xs font-bold text-white">{event.title}</h4>
                          <p className="text-[11px] text-gray-400">
                            📅 {event.dateText} às {event.timeText}
                          </p>
                          {event.meetingUrl && (
                            <p className="text-[10px] text-[#0FFF55] font-mono truncate max-w-xs">{event.meetingUrl}</p>
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDeleteEvent(event.id)}
                        className="p-2 hover:bg-red-500/20 text-red-400 rounded-lg cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TAB 5: SUPORTE & FAQ */}
            {activeTab === 'suporte' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-[#0FFF55]" />
                    <span>Canais de Suporte & FAQ</span>
                  </h2>
                  <p className="text-xs text-gray-400 mt-1">
                    Defina os canais de contato e tire dúvidas frequentes dos seus alunos.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-[#181a20] p-4 rounded-xl border border-white/5">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">E-mail de Suporte</label>
                    <input
                      type="text"
                      value={formData.support?.email || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          support: { ...formData.support, email: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                      placeholder="suporte@mentoria.com"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">WhatsApp de Suporte</label>
                    <input
                      type="text"
                      value={formData.support?.whatsapp || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          support: { ...formData.support, whatsapp: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                      placeholder="https://wa.me/5511..."
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Telegram (opcional)</label>
                    <input
                      type="text"
                      value={formData.support?.telegram || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          support: { ...formData.support, telegram: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                      placeholder="https://t.me/..."
                    />
                  </div>
                </div>

                {/* Gerenciador de Perguntas Frequentes (FAQ) */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-white">Perguntas Frequentes (FAQ)</h3>

                  <div className="bg-[#17181c] p-4 rounded-xl border border-white/10 space-y-3">
                    <h4 className="text-xs font-bold text-[#0FFF55]">➕ Adicionar Pergunta Frequente</h4>
                    <input
                      type="text"
                      value={newFaqQuestion}
                      onChange={(e) => setNewFaqQuestion(e.target.value)}
                      placeholder="Pergunta (Ex: Por quanto tempo tenho acesso?)"
                      className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                    />
                    <textarea
                      value={newFaqAnswer}
                      onChange={(e) => setNewFaqAnswer(e.target.value)}
                      placeholder="Resposta detalhada..."
                      rows={2}
                      className="w-full px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                    />
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleAddFaq}
                        className="px-4 py-1.5 rounded-lg bg-[#0FFF55] hover:bg-[#0dd149] text-black text-xs font-bold cursor-pointer"
                      >
                        Adicionar FAQ
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {(formData.support?.faq || []).map((faq, idx) => (
                      <div
                        key={idx}
                        className="flex items-start justify-between p-3 rounded-xl bg-[#17181c] border border-white/10"
                      >
                        <div className="space-y-1">
                          <p className="text-xs font-bold text-white">❓ {faq.question}</p>
                          <p className="text-xs text-gray-400">💡 {faq.answer}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteFaq(idx)}
                          className="p-1.5 hover:bg-red-500/20 text-red-400 rounded-lg cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* TAB 6: ALUNOS & ACESSOS */}
            {activeTab === 'alunos' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#0FFF55]" />
                    <span>Alunos & Controle de Acesso</span>
                  </h2>
                  <p className="text-xs text-gray-400 mt-1">
                    Gerencie a lista de alunos autorizados a assistir os conteúdos exclusivos desta mentoria.
                  </p>
                </div>

                {/* Form Add Aluno */}
                <div className="bg-[#17181c] p-4 rounded-xl border border-white/10 space-y-3">
                  <h3 className="text-xs font-bold text-[#0FFF55]">➕ Conceder Acesso a Novo Aluno</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={newStudentName}
                      onChange={(e) => setNewStudentName(e.target.value)}
                      placeholder="Nome Completo do Aluno *"
                      className="px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                    />
                    <input
                      type="email"
                      value={newStudentEmail}
                      onChange={(e) => setNewStudentEmail(e.target.value)}
                      placeholder="E-mail de Acesso *"
                      className="px-3 py-2 bg-[#101114] border border-white/10 rounded-lg text-xs text-white"
                    />
                  </div>
                  <div className="flex justify-end pt-1">
                    <button
                      type="button"
                      onClick={handleAddStudent}
                      className="px-4 py-1.5 rounded-lg bg-[#0FFF55] hover:bg-[#0dd149] text-black text-xs font-bold cursor-pointer"
                    >
                      Adicionar Aluno
                    </button>
                  </div>
                </div>

                {/* Tabela de Alunos */}
                <div className="bg-[#17181c] border border-white/10 rounded-xl overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-[#101114] border-b border-white/10 text-gray-400 uppercase text-[10px]">
                      <tr>
                        <th className="p-3">Aluno</th>
                        <th className="p-3">E-mail</th>
                        <th className="p-3">Data de Entrada</th>
                        <th className="p-3 text-right">Ação</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {(formData.students || []).map((student) => (
                        <tr key={student.id} className="hover:bg-white/5 transition-colors">
                          <td className="p-3 font-semibold text-white flex items-center gap-2">
                            <img
                              src={student.avatarUrl}
                              alt={student.name}
                              className="w-6 h-6 rounded-full object-cover"
                            />
                            <span>{student.name}</span>
                          </td>
                          <td className="p-3 text-gray-400 font-mono">{student.email}</td>
                          <td className="p-3 text-gray-500">{student.joinedAtText}</td>
                          <td className="p-3 text-right">
                            <button
                              type="button"
                              onClick={() => handleDeleteStudent(student.id)}
                              className="p-1.5 hover:bg-red-500/20 text-red-400 rounded-lg cursor-pointer"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Botão de Salvar no Rodapé em Todas as Abas */}
          {renderSaveFooter()}
        </main>
      </div>

      {/* Modal QR Code */}
      <BioQRCodeModal
        isOpen={isQrModalOpen}
        onClose={() => setIsQrModalOpen(false)}
        bioUrl={bioPageUrl}
        mentorName={formData.mentorName}
      />
    </div>
  );
};
